import { useCallback, useEffect, useRef } from 'preact/hooks';

import WebApp from '@twa-dev/sdk';

import {
  EXCLUSIVE_MARKET_SOURCE,
  LIGHT_MARKET_SOURCE,
  MARKET_SOURCE,
} from '@/app/lib/localization/constants';
import { useIntl } from '@/app/lib/localization/hooks/use-intl';

import { getOrCreateElement, removeElement } from '@/shared/helpers/dom';
import { isTMA } from '@/shared/helpers/telegram/is-tma';
import { returnToMarket } from '@/shared/helpers/telegram/return-to-market';
import { useStartAppArgs } from '@/shared/stores/app-store/app-store';

import {
  DEFAULT_TOP_SAFE_AREA_OFFSET_PX,
  DEV_BACK_BUTTON_ID,
  TOP_SAFE_AREA_WRAPPER_ID,
} from './constants';

/**
 * Custom hook to handle Telegram's WebApp back button.
 * When gameControlsBackButton is true, the wrapper yields full control to the game:
 * - goBack is suppressed so returning to marketplace does not trigger
 * - back button visibility is not overridden by wrapper logic
 */
export const useTelegramBackButton = ({
  gameControlsBackButton = false,
}: {
  gameControlsBackButton?: boolean;
} = {}): void => {
  const startAppArgs = useStartAppArgs();

  const t = useIntl();

  const IS_DEV_ENV = import.meta.env.DEV && !isTMA();

  const gameControlsBackButtonRef = useRef(gameControlsBackButton);
  useEffect(() => {
    gameControlsBackButtonRef.current = gameControlsBackButton;
  }, [gameControlsBackButton]);

  /**
   * Show the native back button if the wrapper is opened from the marketplace.
   * For android show native back button only if user pressed PLAY button,
   * because navigate to another webapp is not working without any interaction with the webapp
   */
  const hasBackAction = startAppArgs.src
    ? [MARKET_SOURCE, LIGHT_MARKET_SOURCE, EXCLUSIVE_MARKET_SOURCE].includes(
        startAppArgs.src
      )
    : false;

  const goBack = useCallback(() => {
    if (gameControlsBackButtonRef.current) return;
    returnToMarket({ needAsk: true, t });
  }, [t]);

  /**
   * Configures the fake back button in development mode.
   */
  const setupDevBackButton = useCallback(
    (hasBackAction: boolean) => {
      if (!IS_DEV_ENV) return;

      const container = getOrCreateElement(TOP_SAFE_AREA_WRAPPER_ID);
      container.className = 'top-safe-area-wrapper';

      document.documentElement.style.setProperty(
        '--tg-content-safe-area-inset-top',
        `${DEFAULT_TOP_SAFE_AREA_OFFSET_PX}px`
      );

      const button = getOrCreateElement(DEV_BACK_BUTTON_ID, container, 'button');
      button.className = 'dev-back-button';
      button.classList.toggle('close', !hasBackAction);
      button.textContent = hasBackAction ? t('Back') : t('Close');

      if (hasBackAction) button.addEventListener('click', goBack);
    },
    [IS_DEV_ENV, t, goBack]
  );

  useEffect(() => {
    WebApp.BackButton.onClick(goBack);
    document.getElementById(DEV_BACK_BUTTON_ID)?.addEventListener('click', goBack);

    return () => {
      WebApp.BackButton.offClick(goBack);
      document.getElementById(DEV_BACK_BUTTON_ID)?.removeEventListener('click', goBack);
      WebApp.BackButton.hide();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gameControlsBackButton) return;
    if (hasBackAction) {
      WebApp.BackButton.show();
    } else {
      WebApp.BackButton.hide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, startAppArgs.src, goBack, gameControlsBackButton]);

  useEffect(() => {
    return () => {
      removeElement(
        DEV_BACK_BUTTON_ID,
        document.getElementById(TOP_SAFE_AREA_WRAPPER_ID) as HTMLElement
      );
      WebApp.BackButton.hide();
    };
  }, []);

  useEffect(() => {
    setupDevBackButton(hasBackAction);
  }, [IS_DEV_ENV, hasBackAction, setupDevBackButton, t]);
};
